import React, { memo, ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Table, VehicleTableActions } from 'modules/core/components';
import { TablePlaceholderContent } from 'modules/core/components/Table/TablePlaceholderContent';
import { VehicleFilters } from 'modules/core/components/VehicleFilters';
import { VehicleDetailsDialog } from 'components/Modals/VehicleDetailsDialog';
import { VehicleAdjustmentDialog } from 'components/Modals/VehicleAdjustmentDialog';
import { SendReportDialog } from 'components/Modals/SendReportDialog';
import { useModalState, useTablePagination } from 'modules/core/hooks';
import { MODAL_DIALOG_ID } from 'modules/core/constants';
import {
  IAvailableFilters,
  ILenderVehicle,
  IRACVehicle,
  IUser,
  IVehicle,
  TFetchingVehicleFlagsSelector,
  TReportSelector,
} from 'modules/core/types';
import { TableDropdowns } from 'modules/ProbabilityEstimator/styles';
import { useTableCallback } from 'modules/ProbabilityEstimator/hooks';
import { TableName, VehicleTablePlaceholderType } from 'modules/core/enums';
import { ITablePlaceholder } from 'modules/core/components/Table/TablePlaceholderContent/TablePlaceholderContent';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { TableData } from 'modules/core/components/Table/types';
import { getFeaturesList, getIsMissingCostInfo } from 'store/selectors/ui.selector';
import { Row } from 'react-table';
import { useSelector } from 'react-redux';
import {
  LENDER_VEHICLE_TABLE_COLUMN_ID,
  RAC_VEHICLE_TABLE_COLUMN_ID,
  VEHICLE_TABLE_COLUMN_ID,
} from 'modules/core/components/VehicleTable/defaultVehicleTableColumns';
import { getCurrentLenderId, getLenderState } from 'store/selectors/lenders.selector';
import { setSortedVehiclesData } from 'store/reducers/lenders.reducer';
import { setSortedVehiclesData as setRACSortedVehiclesData } from 'store/reducers/RACAffordabilityEngine.reducer';
import { IDealSourceData } from 'modules/core/types/dealSource';
import { AppDispatch } from 'store/store';
import { useDispatch } from 'react-redux';
import { getUser } from 'store/selectors/auth.selector';
import { logToDatadog } from 'utils/datadog';
import { DatadogEvents } from 'utils/datadogEvents';

import { Container } from './styles';
import { PROBABILITY_ESTIMATOR_FIELDS } from '../ProbabilityEstimator/probabilityEstimator.const';
import { useColumns } from './hooks/useColumns';
import { useSortedData } from './hooks/useSortedData';

interface IVehicleSearch {
  data: IVehicle[] | IRACVehicle[] | ILenderVehicle[];
  reportDataSelector?: TReportSelector;
  tableSubmitCallback: VoidFunction;
  defaultPlaceholderTitle: string;
  previewPlaceholder: string | ReactElement;
  isTableFiltersDisabled?: boolean;
  isTableActionsDisabled?: boolean;
  isTableView: boolean;
  setIsTableView: ActionCreatorWithPayload<boolean, string>;
  areRequiredFieldsFilledIn?: boolean;
  fetchingVehicleFlagsSelector: TFetchingVehicleFlagsSelector;
  CustomVehicleCard?: any;
  tableName: TableName;
  isFilterByStockNumber?: boolean;
  notFoundVehicles?: string[];
  notAvailableVehicles?: string[];
  totalCount?: number;
  fetchData?: any;
  isServerPagination?: boolean;
  selectedRows?: TableData[];
  skipAutoStateReset?: boolean;
  handleOpenDialog?: () => void;
  isRac?: boolean;
  isLender?: boolean;
  showNoVehicleErrorMessage?: boolean;
  showNoVehicleInInventoryErrorMessage?: boolean;
  showNoAddedVehicleErrorMessage?: boolean;
  showNoResultsAndDealSourceVehicle?: boolean;
  isMakeItWork?: boolean;
  dealSourceData?: IDealSourceData;
  tier?: number | string;
  tablePlaceholderType: VehicleTablePlaceholderType;
  availableFilters?: IAvailableFilters;
  filterControls?: ReactNode;
}

const VehicleSearchTable: React.FC<IVehicleSearch> = ({
  data,
  reportDataSelector,
  tableSubmitCallback,
  defaultPlaceholderTitle,
  previewPlaceholder,
  CustomVehicleCard,
  isTableFiltersDisabled,
  isTableActionsDisabled,
  isTableView,
  setIsTableView,
  areRequiredFieldsFilledIn,
  fetchingVehicleFlagsSelector,
  tableName,
  isFilterByStockNumber,
  notAvailableVehicles,
  notFoundVehicles,
  totalCount,
  fetchData,
  isServerPagination = false,
  selectedRows,
  skipAutoStateReset = false,
  handleOpenDialog,
  isRac = false,
  isLender = false,
  showNoVehicleErrorMessage = false,
  showNoVehicleInInventoryErrorMessage = false,
  showNoAddedVehicleErrorMessage = false,
  showNoResultsAndDealSourceVehicle = false,
  isMakeItWork = false,
  dealSourceData = {},
  tier,
  tablePlaceholderType,
  availableFilters,
  filterControls,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { pageCount, paginationCallback } = useTablePagination(data, totalCount, fetchData, isServerPagination);
  const isCostMissing = useSelector(getIsMissingCostInfo);
  const lenderName = useSelector(getLenderState)?.name;
  const currentLenderId = useSelector(getCurrentLenderId);
  const { isFetchingVehicles, fetchVehiclesError, resetCount } = useSelector(fetchingVehicleFlagsSelector);

  const { is_calculated_book_value } = useSelector(getFeaturesList);

  const [localSortedRows, setLocalSortedRows] = useState<Row<TableData>[]>([]);
  const baseTableCallbacks = useTableCallback();

  const tableSortedData = useMemo(() => localSortedRows.map((row) => row.original), [localSortedRows]);

  const tableCallbacks = useMemo(
    () => ({
      ...baseTableCallbacks,
      sortedRowsCallback: (rows: Row<TableData>[]) => {
        setLocalSortedRows(rows);
      },
    }),
    [baseTableCallbacks],
  );

  const {
    watch,
    formState: { isValid },
  } = useFormContext();

  const selectedTableView = watch(PROBABILITY_ESTIMATOR_FIELDS.TABLE_VIEW);

  const { columns, defaultColumns, customerFriendlyColumns } = useColumns(
    selectedTableView,
    tableName,
    data,
    isMakeItWork,
  );
  const { sortedData } = useSortedData(selectedTableView, tableName, data, isServerPagination);

  useEffect(() => {
    if (tableSortedData.length) {
      isRac && dispatch(setRACSortedVehiclesData(tableSortedData as IRACVehicle[]));
      isLender && dispatch(setSortedVehiclesData(tableSortedData as ILenderVehicle[]));
    }
  }, [dispatch, tableSortedData, isRac, isLender]);

  const currentColumns = useMemo(() => {
    const hiddenColumns: string[] = [];

    if (isCostMissing) {
      hiddenColumns.push(
        RAC_VEHICLE_TABLE_COLUMN_ID.PROFIT,
        RAC_VEHICLE_TABLE_COLUMN_ID.UNIT_COST,
        LENDER_VEHICLE_TABLE_COLUMN_ID.ESTIMATED_PROFIT,
        VEHICLE_TABLE_COLUMN_ID.DEALER_PROFIT,
      );
    }

    if (is_calculated_book_value) {
      hiddenColumns.push(
        VEHICLE_TABLE_COLUMN_ID.BOOK_VALUE,
        VEHICLE_TABLE_COLUMN_ID.VALUATION_GUIDE,
        VEHICLE_TABLE_COLUMN_ID.LTV_RATIO,
        RAC_VEHICLE_TABLE_COLUMN_ID.LTV,
        VEHICLE_TABLE_COLUMN_ID.ADDITIONAL_DOWN_PAYMENT,
        RAC_VEHICLE_TABLE_COLUMN_ID.EXTRA_CASH,
        LENDER_VEHICLE_TABLE_COLUMN_ID.SALESPERSON_BUCKET,
      );
    }

    return hiddenColumns.length > 0 ? columns.filter((column) => !hiddenColumns.includes(column.id)) : columns;
  }, [columns, isCostMissing, is_calculated_book_value]);

  const renderAdditionalTableHeaderComponents = () => (
    <TableDropdowns>
      {reportDataSelector && (
        <VehicleTableActions
          reportDataSelector={reportDataSelector}
          closeDropDown={isTableActionsDisabled}
          isDisabled={isTableActionsDisabled}
        />
      )}
    </TableDropdowns>
  );

  const {
    handleCloseDialog: handleCloseVehicleDetailsDialog,
    isModalDialogActive: isVehicleDetailsDialogActive,
  } = useModalState([MODAL_DIALOG_ID.VEHICLE_DETAILS]);

  const {
    handleCloseDialog: handleCloseVehicleAdjustmentDialog,
    isModalDialogActive: isVehicleAdjustmentDialogActive,
  } = useModalState([MODAL_DIALOG_ID.VEHICLE_ADJUSTMENT]);

  const {
    handleCloseDialog: handleCloseSendReportDialog,
    isModalDialogActive: isSendReportDialogActive,
  } = useModalState([MODAL_DIALOG_ID.SEND_REPORT]);

  const isValidInputData = areRequiredFieldsFilledIn !== undefined ? areRequiredFieldsFilledIn : isValid;

  const user = useSelector(getUser) as IUser;

  useEffect(() => {
    if (tablePlaceholderType === VehicleTablePlaceholderType.ERROR) {
      logToDatadog(
        DatadogEvents.LOG_CATEGORIES[DatadogEvents.FAILED_TO_RETRIEVE_DATA],
        {
          log_category: DatadogEvents.FAILED_TO_RETRIEVE_DATA,
          table_name: tableName,
          error_details: {
            message: fetchVehiclesError?.message,
            status: fetchVehiclesError?.response?.status,
            statusText: fetchVehiclesError?.response?.statusText,
            data: fetchVehiclesError?.response?.data,
          },
        },
        user,
      );
    }
  }, [tablePlaceholderType, tableName, user, fetchVehiclesError]);

  const tablePlaceholder = useMemo(() => {
    const tablePlaceholderMapProps = new Map<VehicleTablePlaceholderType, ITablePlaceholder>([
      [VehicleTablePlaceholderType.ERROR, { title: t('common.errorMessages.failedRequestMessage') }],
      [VehicleTablePlaceholderType.LOADING, { isFetching: true }],
      [VehicleTablePlaceholderType.DEFAULT, { title: defaultPlaceholderTitle }],
      [VehicleTablePlaceholderType.SEARCH, { title: previewPlaceholder }],
      [VehicleTablePlaceholderType.NEED_STOCK, { title: t('components.probabilityEstimator.needStock') }],
    ]);

    const tablePlaceholderProps = tablePlaceholderMapProps.get(tablePlaceholderType);

    return <TablePlaceholderContent {...tablePlaceholderProps} />;
  }, [t, defaultPlaceholderTitle, previewPlaceholder, tablePlaceholderType]);

  return (
    <Container>
      <Table
        handleOpenDialog={handleOpenDialog}
        tableSubmitCallback={tableSubmitCallback}
        tableCallbacks={tableCallbacks}
        data={sortedData}
        columns={currentColumns}
        defaultColumns={defaultColumns}
        customerFriendlyColumns={customerFriendlyColumns}
        pageCount={pageCount}
        fetchRequestCallback={paginationCallback}
        title={t('components.table.recommendedVehicles')}
        renderAdditionalTableHeaderComponents={renderAdditionalTableHeaderComponents}
        selectable
        withCardView
        {...(data?.length && isValidInputData ? {} : { tablePlaceholder })}
        tablePlaceholderType={tablePlaceholderType}
        isTableFiltersDisabled={isTableFiltersDisabled}
        isTableActionsDisabled={isTableActionsDisabled}
        isTableView={isTableView}
        setIsTableView={setIsTableView}
        CustomVehicleCard={CustomVehicleCard}
        totalCount={totalCount}
        isServerPagination={isServerPagination}
        selectedRows={selectedRows}
        skipAutoStateReset={skipAutoStateReset}
        resetCount={resetCount}
        tableName={tableName}
        showNoVehicleErrorMessage={showNoVehicleErrorMessage}
        showNoVehicleInInventoryErrorMessage={showNoVehicleInInventoryErrorMessage}
        showNoResultsAndDealSourceVehicle={
          showNoResultsAndDealSourceVehicle && tablePlaceholderType !== VehicleTablePlaceholderType.LOADING
        }
        showNoAddedVehicleErrorMessage={showNoAddedVehicleErrorMessage}
        isMakeItWork={isMakeItWork}
        dealSourceData={dealSourceData as IDealSourceData}
        tier={tier}
        lenderName={lenderName || undefined}
        currentLenderId={currentLenderId || undefined}
      >
        <VehicleFilters
          isDisabled={isTableFiltersDisabled}
          isFilterButtonDisabled={
            !isValidInputData || isFetchingVehicles || tablePlaceholderType === VehicleTablePlaceholderType.LOADING
          }
          isFilterByStockNumber={isFilterByStockNumber}
          notAvailableVehicles={notAvailableVehicles}
          notFoundVehicles={notFoundVehicles}
          isRac={isRac}
          isLender={isLender}
          availableFilters={availableFilters}
          filterControls={filterControls}
        />
      </Table>
      <VehicleDetailsDialog show={isVehicleDetailsDialogActive} onHide={handleCloseVehicleDetailsDialog()} />
      <VehicleAdjustmentDialog
        show={isVehicleAdjustmentDialogActive}
        onHide={handleCloseVehicleAdjustmentDialog()}
        onApplyCallback={tableSubmitCallback}
      />
      {reportDataSelector && (
        <SendReportDialog
          show={isSendReportDialogActive}
          onHide={handleCloseSendReportDialog()}
          reportDataSelector={reportDataSelector}
        />
      )}
    </Container>
  );
};

export const VehicleSearchTableMemo = memo(VehicleSearchTable);
